import React, { Suspense, lazy, useEffect } from 'react';
// Frontend deployment test - $(Get-Date) - SSL ready - Fixed deployment action - DNS working
// Trigger deployment - testing Static Web App configuration
// TEST: React app should load - deployment verification
// PREVIEW: Trigger deployment to preview environment
// PRODUCTION: Trigger deployment to production environment
// Deployment trigger: Production agent onboarding fix - Updated agent instructions
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import AuthErrorDisplay from './components/AuthErrorDisplay';
import SplashPage from './components/SplashPage';
import RootRoute from './components/RootRoute';
import GlobalNotifications from './components/GlobalNotifications';
import TermsReacceptanceGate from './components/TermsReacceptanceGate';
import { initializeErrorHandlers } from './utils/errorHandler';
import mixpanelService from './utils/mixpanel';
import './App.css';
import './MobileImprovements.css';

// Route-level code splitting to reduce initial UI load cost.
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const PasswordReset = lazy(() => import('./components/PasswordReset'));
const EmailVerification = lazy(() => import('./components/EmailVerification'));
const GoogleCallback = lazy(() => import('./components/GoogleCallback'));
const OutlookCallback = lazy(() => import('./components/OutlookCallback'));
const EmailOptOut = lazy(() => import('./components/EmailOptOut'));
const TermsOfUsePage = lazy(() => import('./components/TermsOfUsePage'));
const PrivacyPolicyPage = lazy(() => import('./components/PrivacyPolicyPage'));

const Navbar = lazy(() => import('./components/Navbar'));
const Calendar = lazy(() => import('./components/Calendar'));
const Lobby = lazy(() => import('./components/Lobby'));
const Profile = lazy(() => import('./components/Profile'));
const AvailabilityWindows = lazy(() => import('./components/AvailabilityWindows'));
const Friends = lazy(() => import('./components/Friends'));
const Chat = lazy(() => import('./components/Chat'));
const ChatOld = lazy(() => import('./components/ChatOld'));
const UpcomingEvents = lazy(() => import('./components/UpcomingEvents'));
const ConciergeDashboard = lazy(() => import('./components/ConciergeDashboard'));
const Communities = lazy(() => import('./components/Communities'));
const CommunityDetail = lazy(() => import('./components/CommunityDetail'));
const AcceptInvite = lazy(() => import('./components/AcceptInvite'));
const CommunityJoin = lazy(() => import('./components/CommunityJoin'));
const CommunityInviteLobby = lazy(() => import('./components/CommunityInviteLobby'));
const Concierge = lazy(() => import('./components/Concierge'));
const MemberPortrait = lazy(() => import('./components/MemberPortrait/MemberPortrait'));
const PersonalityQuiz = lazy(() => import('./components/PersonalityQuiz/PersonalityQuiz'));
const AdminMatchingDashboard = lazy(() => import('./components/AdminMatchingDashboard'));
const WaitlistPage = lazy(() => import('./components/WaitlistPage'));
const WelcomeOnboarding = lazy(() => import('./components/WelcomeOnboarding'));

const RouteLoadingFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
    <div className="spinner-border text-muted" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const WAITLIST_ALLOWED_PATHS = new Set(['/waitlist']);

// Page View Tracker Component
const PageViewTracker = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      // Track page views for authenticated users
      const pageName = location.pathname === '/' ? 'Calendar' :
        location.pathname.replace('/', '').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());

      mixpanelService.trackPageView(pageName, {
        path: location.pathname,
        search: location.search
      });
    }
  }, [location, isAuthenticated]);

  return null;
};

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, token, currentUser, onboardingStatus } = useAuth();
  const location = useLocation();

  // Debug logging
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('PrivateRoute state:', { isAuthenticated, loading, hasToken: !!token, hasCurrentUser: !!currentUser });
  }

  // Show loading spinner while authentication or onboarding status is being checked
  if (loading || (isAuthenticated && onboardingStatus?.loading)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-muted" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Preserve original destination (including query params like invite token) so login can redirect back.
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isTermsHardBlocked = !!currentUser?.terms_status?.requires_reacceptance
    && currentUser?.terms_status?.enforcement_mode === 'hard';

  if (isTermsHardBlocked) {
    return <TermsReacceptanceGate />;
  }

  const registrationIncomplete =
    !!currentUser && currentUser.registration_profile_completed === false;
  const onRegistrationCompletionPath =
    location.pathname === '/register' || location.pathname.startsWith('/registration/');

  if (registrationIncomplete && !onRegistrationCompletionPath) {
    return <Navigate to="/register" replace />;
  }

  const isWaitlistedUser = !!currentUser?.is_waitlisted;
  const welcomeFlowDone =
    onboardingStatus.profileComplete && onboardingStatus.waitlistHandoffComplete;
  const isOnboardingIncomplete = !welcomeFlowDone;
  const isCurrentlyOnboarding = location.pathname === '/welcome';

  if (isOnboardingIncomplete && !isCurrentlyOnboarding && !onboardingStatus.loading) {
    return <Navigate to="/welcome" replace />;
  }

  if (isWaitlistedUser && !isOnboardingIncomplete && !WAITLIST_ALLOWED_PATHS.has(location.pathname)) {
    return <Navigate to="/waitlist" replace />;
  }

  if (!isWaitlistedUser && location.pathname === '/waitlist') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const MemberPortraitRouteLayout = () => {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <div>
        <Navbar />
        <div className="container page-shell mb-5">
          <MemberPortrait />
        </div>
      </div>
    </Suspense>
  );
};

const App = () => {
  useEffect(() => {
    // Initialize global error handlers
    initializeErrorHandlers();

    // Cleanup on unmount
    return () => {
      // Note: cleanupErrorHandlers would be called here if needed
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <PageViewTracker />
          <div className="app">
            <AuthErrorDisplay />
            <GlobalNotifications />
            <Routes>
              <Route path="/login" element={<Suspense fallback={<RouteLoadingFallback />}><Login /></Suspense>} />
              <Route path="/login/referral/:referralCode" element={<Suspense fallback={<RouteLoadingFallback />}><Login /></Suspense>} />
              <Route path="/register" element={<Suspense fallback={<RouteLoadingFallback />}><Register /></Suspense>} />
              <Route path="/registration/referral/:referralCode" element={<Suspense fallback={<RouteLoadingFallback />}><Register /></Suspense>} />
              <Route path="/forgot-password" element={<Suspense fallback={<RouteLoadingFallback />}><PasswordReset /></Suspense>} />
              <Route path="/verify-email" element={<Suspense fallback={<RouteLoadingFallback />}><EmailVerification /></Suspense>} />
              <Route path="/google-callback" element={<Suspense fallback={<RouteLoadingFallback />}><GoogleCallback /></Suspense>} />
              <Route path="/outlook-callback" element={<Suspense fallback={<RouteLoadingFallback />}><OutlookCallback /></Suspense>} />
              <Route path="/email-optout" element={<Suspense fallback={<RouteLoadingFallback />}><EmailOptOut /></Suspense>} />
              <Route path="/terms" element={<Suspense fallback={<RouteLoadingFallback />}><TermsOfUsePage /></Suspense>} />
              <Route path="/privacy" element={<Suspense fallback={<RouteLoadingFallback />}><PrivacyPolicyPage /></Suspense>} />
              <Route path="/waitlist" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <WaitlistPage />
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/" element={<RootRoute />} />
              <Route path="/calendar" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <Calendar />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/lobby" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <Lobby />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/splash" element={<SplashPage />} />
              <Route path="/join/:token" element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <CommunityInviteLobby />
                </Suspense>
              } />
              <Route path="/profile" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <Profile />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/availability-windows" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <AvailabilityWindows />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/friends" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <Friends />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/chat" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <Chat />
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/chatold" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <ChatOld />
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/upcoming-events" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <UpcomingEvents />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/welcome" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <WelcomeOnboarding />
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/onboarding" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <ConciergeDashboard />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/dashboard" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <ConciergeDashboard />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/communities" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <Communities />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/communities/join/:inviteCode" element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <CommunityJoin />
                </Suspense>
              } />
              <Route path="/communities/:id" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <CommunityDetail />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/chief-of-staff" element={<Navigate to="/interview" replace />} />
              <Route path="/concierge_direct" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <Concierge />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/interview" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell">
                        <Concierge />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/MemberPortrait" element={
                <PrivateRoute>
                  <MemberPortraitRouteLayout />
                </PrivateRoute>
              } />
              <Route path="/personality-quiz" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div className="personality-quiz-route">
                      <Navbar />
                      <div className="container page-shell full-height-shell">
                        <PersonalityQuiz />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/admin/operations" element={
                <PrivateRoute>
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <div>
                      <Navbar />
                      <div className="container page-shell mb-5">
                        <AdminMatchingDashboard />
                      </div>
                    </div>
                  </Suspense>
                </PrivateRoute>
              } />
              <Route path="/admin/matching" element={<Navigate to="/admin/operations" replace />} />
              <Route path="/accept-invite" element={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <AcceptInvite />
                </Suspense>
              } />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export { PrivateRoute };
export default App;
